import React from "react";
import { Loader } from "lucide-react";

interface ButtonProps {
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent) => void;
  type?: "button" | "submit" | "reset";
  className?: string;
  disabled?: boolean;
  isLoading?: boolean;
  variant?: "primary" | "secondary" | "outlined";
  fullWidth?: boolean;
  icon?: React.ReactNode;
  iconPosition?: "left" | "right";
}

const Button: React.FC<ButtonProps> = ({
  children,
  onClick,
  type = "button",
  className = "",
  disabled = false,
  isLoading = false,
  variant = "primary",
  fullWidth = false,
  icon = null,
  iconPosition = "left",
}) => {
  const baseStyles =
    "py-2 px-4 rounded font-medium transition duration-200 focus:outline-none active:scale-95 active:shadow-inner";

  const widthStyles = fullWidth ? "w-full" : "";

  const variantStyles = {
    primary:
      "bg-black text-white hover:bg-blue-800 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50",
    secondary:
      "bg-gray-200 text-gray-800 hover:bg-gray-300 focus:ring-2 focus:ring-gray-400 focus:ring-opacity-50",
    outlined:
      "bg-transparent text-black border border-black hover:bg-gray-100 focus:ring-2 focus:ring-black focus:ring-opacity-50",
  };

  const disabledStyles =
    disabled || isLoading ? "opacity-50 cursor-not-allowed hover:bg-black" : "";

  const buttonStyles = `${baseStyles} ${widthStyles} ${variantStyles[variant]} ${disabledStyles} ${className}`;

  return (
    <button
      type={type}
      className={buttonStyles}
      onClick={onClick}
      disabled={disabled || isLoading}
    >
      {isLoading ? (
        <span className="flex items-center justify-center gap-2">
          <Loader className="animate-spin h-4 w-4" />
          <span>{children}</span>
        </span>
      ) : (
        <span className="flex items-center justify-center gap-2">
          {icon && iconPosition === "left" && <span>{icon}</span>}
          <span>{children}</span>
          {icon && iconPosition === "right" && <span>{icon}</span>}
        </span>
      )}
    </button>
  );
};

export default Button;
